import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { ManualEntryForm } from "./ManualEntryForm";
import { ResearchResults } from "./ResearchResults";
import { DickinsonCountyScraper } from "./DickinsonCountyScraper";
import { CsvImport } from "./CsvImport";
import { DickinsonTextParser } from "./DickinsonTextParser";
import { AllenCountyTextParser } from "./AllenCountyTextParser";
import { LorainCountyTextParser } from "./LorainCountyTextParser";
import { MedinaCountyTextParser } from "./MedinaCountyTextParser";
import { MercerCountyTextParser } from "./MercerCountyTextParser";
import { MuskingumCountyTextParser } from "./MuskingumCountyTextParser";
import { PortageCountyTextParser } from "./PortageCountyTextParser";
import { ComalCountyTextParser } from "./ComalCountyTextParser";
import { WashtenawCountyTextParser } from "./WashtenawCountyTextParser";
import { NewaygoCountyTextParser } from "./NewaygoCountyTextParser";
import { CountyFusionTextParser } from "./CountyFusionTextParser";
import { CuyahogaCountyTextParser } from "./CuyahogaCountyTextParser";
import { WarrenCountyTextParser } from "./WarrenCountyTextParser";
import { WhatcomCountyTextParser } from "./WhatcomCountyTextParser";
import { QuickCaptureMode } from "./QuickCaptureMode";
import { DirectScreenshotCapture } from "./DirectScreenshotCapture";
import { DataExportTool } from "./DataExportTool";
import type { SearchResults } from "./types";

// CountyFusion/GovOS counties - these all use the same parser
const COUNTY_FUSION_COUNTIES = [
  'ashland', 'auglaize', 'belmont', 'butler', 'carroll', 'champaign', 
  'clark', 'clermont', 'coshocton', 'fayette', 'franklin', 'fulton', 
  'greene', 'guernsey', 'hancock', 'hardin', 'harrison', 'henry', 
  'highland', 'jefferson', 'licking', 'logan', 'monroe', 'morgan', 
  'ottawa', 'pickaway', 'pike', 'preble', 'richland', 'ross', 
  'sandusky', 'seneca', 'shelby', 'stark', 'tuscarawas', 'vanwert', 
  'washington', 'wayne'
];

// Texas ROAM counties
const TEXAS_ROAM_COUNTIES = ['comal', 'guadalupe', 'kendall', 'kerr', 'bandera'];

const COUNTIES = [
  { value: 'allen', label: 'Allen County, OH' },
  { value: 'ashland', label: 'Ashland County, OH' },
  { value: 'asotin', label: 'Asotin County, WA' },
  { value: 'auglaize', label: 'Auglaize County, OH' },
  { value: 'bandera', label: 'Bandera County, TX' },
  { value: 'belmont', label: 'Belmont County, OH' },
  { value: 'berrien', label: 'Berrien County, MI' },
  { value: 'butler', label: 'Butler County, OH' },
  { value: 'calhoun', label: 'Calhoun County, MI' },
  { value: 'carroll', label: 'Carroll County, OH' },
  { value: 'champaign', label: 'Champaign County, OH' },
  { value: 'clark', label: 'Clark County, OH' },
  { value: 'clermont', label: 'Clermont County, OH' },
  { value: 'comal', label: 'Comal County, TX' },
  { value: 'coshocton', label: 'Coshocton County, OH' },
  { value: 'cuyahoga', label: 'Cuyahoga County, OH' },
  { value: 'dickinson', label: 'Dickinson County, MI' },
  { value: 'douglas', label: 'Douglas County, WA' },
  { value: 'eaton', label: 'Eaton County, MI' },
  { value: 'fayette', label: 'Fayette County, OH' },
  { value: 'ferry', label: 'Ferry County, WA' },
  { value: 'franklin', label: 'Franklin County, OH' },
  { value: 'fulton', label: 'Fulton County, OH' },
  { value: 'greene', label: 'Greene County, OH' },
  { value: 'guadalupe', label: 'Guadalupe County, TX' },
  { value: 'guernsey', label: 'Guernsey County, OH' },
  { value: 'hancock', label: 'Hancock County, OH' },
  { value: 'hardin', label: 'Hardin County, OH' },
  { value: 'harrison', label: 'Harrison County, OH' },
  { value: 'henry', label: 'Henry County, OH' },
  { value: 'highland', label: 'Highland County, OH' },
  { value: 'hillsdale', label: 'Hillsdale County, MI' },
  { value: 'isabella', label: 'Isabella County, MI' },
  { value: 'jefferson', label: 'Jefferson County, OH' },
  { value: 'kendall', label: 'Kendall County, TX' },
  { value: 'kerr', label: 'Kerr County, TX' },
  { value: 'lawrence', label: 'Lawrence County, PA' },
  { value: 'lenawee', label: 'Lenawee County, MI' },
  { value: 'lewis', label: 'Lewis County, WA' },
  { value: 'licking', label: 'Licking County, OH' },
  { value: 'logan', label: 'Logan County, OH' },
  { value: 'lorain', label: 'Lorain County, OH' },
  { value: 'madison', label: 'Madison County, OH' },
  { value: 'mecosta', label: 'Mecosta County, MI' },
  { value: 'medina', label: 'Medina County, OH' },
  { value: 'mercer', label: 'Mercer County, OH' },
  { value: 'monroe', label: 'Monroe County, OH' },
  { value: 'morgan', label: 'Morgan County, OH' },
  { value: 'muskingum', label: 'Muskingum County, OH' },
  { value: 'newaygo', label: 'Newaygo County, MI' },
  { value: 'noble', label: 'Noble County, OH' },
  { value: 'ottawa', label: 'Ottawa County, OH' },
  { value: 'pickaway', label: 'Pickaway County, OH' },
  { value: 'pike', label: 'Pike County, OH' },
  { value: 'portage', label: 'Portage County, OH' },
  { value: 'preble', label: 'Preble County, OH' },
  { value: 'presqueisle', label: 'Presque Isle County, MI' },
  { value: 'randall', label: 'Randall County, TX' },
  { value: 'richland', label: 'Richland County, OH' },
  { value: 'ross', label: 'Ross County, OH' },
  { value: 'sandusky', label: 'Sandusky County, OH' },
  { value: 'sanjuan', label: 'San Juan County, WA' },
  { value: 'seneca', label: 'Seneca County, OH' },
  { value: 'shelby', label: 'Shelby County, OH' },
  { value: 'skagit', label: 'Skagit County, WA' },
  { value: 'stark', label: 'Stark County, OH' },
  { value: 'travis', label: 'Travis County, TX' },
  { value: 'tuscarawas', label: 'Tuscarawas County, OH' },
  { value: 'vanwert', label: 'Van Wert County, OH' },
  { value: 'warren', label: 'Warren County, OH' },
  { value: 'washtenaw', label: 'Washtenaw County, MI' },
  { value: 'washington', label: 'Washington County, OH' },
  { value: 'wayne', label: 'Wayne County, OH' },
  { value: 'whatcom', label: 'Whatcom County, WA' },
  { value: 'williamson', label: 'Williamson County, TX' },
  { value: 'other', label: 'Other County' }
];

export function CountyResearchPage() {
  const [selectedCounty, setSelectedCounty] = useState('stark');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRecordSaved = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const renderImportTab = () => {
    // Texas ROAM counties
    if (TEXAS_ROAM_COUNTIES.includes(selectedCounty)) {
      return <ComalCountyTextParser county={selectedCounty} onRecordsSaved={handleRecordSaved} />;
    }

    // CountyFusion counties (38 Ohio counties)
    if (COUNTY_FUSION_COUNTIES.includes(selectedCounty)) {
      return <CountyFusionTextParser county={selectedCounty} onImportComplete={handleRecordSaved} />;
    }

    // Special parsers for specific counties
    switch (selectedCounty) {
      case 'dickinson':
        return <DickinsonTextParser onImportComplete={handleRecordSaved} />;
      case 'allen':
        return <AllenCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'lorain':
        return <LorainCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'medina':
        return <MedinaCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'mercer':
        return <MercerCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'muskingum':
        return <MuskingumCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'portage':
        return <PortageCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'washtenaw':
        return <WashtenawCountyTextParser />;
      case 'newaygo':
        return <NewaygoCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'cuyahoga':
        return <CuyahogaCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'warren':
        return <WarrenCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'whatcom':
      case 'sanjuan':
      case 'asotin':
      case 'ferry':
        return <WhatcomCountyTextParser onRecordsSaved={handleRecordSaved} />;
      default:
        return <CsvImport onImportComplete={handleRecordSaved} />;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">County Research</h1>
        <p className="text-muted-foreground mt-1">
          Unified interface for county property research across all counties
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select County</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-xs">
            <Label htmlFor="county-select">Working County</Label>
            <Select value={selectedCounty} onValueChange={setSelectedCounty}>
              <SelectTrigger id="county-select">
                <SelectValue placeholder="Select a county" />
              </SelectTrigger>
              <SelectContent>
                {COUNTIES.map(county => (
                  <SelectItem key={county.value} value={county.value}>
                    {county.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="import" className="w-full">
        <TabsList>
          <TabsTrigger value="import">📥 Import Data</TabsTrigger>
          <TabsTrigger value="screenshot-bot">🤖 Screenshot Bot</TabsTrigger>
          <TabsTrigger value="quick-capture">⚡ Quick Capture</TabsTrigger>
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="automated">Automated Scraping</TabsTrigger>
          <TabsTrigger value="export">📊 Export Tool</TabsTrigger>
          <TabsTrigger value="results">View Records</TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-4">
          {renderImportTab()}
        </TabsContent>

        <TabsContent value="screenshot-bot" className="space-y-4">
          <DirectScreenshotCapture 
            county={selectedCounty}
            onRecordSaved={handleRecordSaved}
          />
        </TabsContent>

        <TabsContent value="quick-capture" className="space-y-4">
          <QuickCaptureMode />
        </TabsContent>

        <TabsContent value="manual" className="space-y-4">
          <ManualEntryForm county={selectedCounty} onRecordSaved={handleRecordSaved} />
        </TabsContent>

        <TabsContent value="automated" className="space-y-4">
          {selectedCounty === 'dickinson' && (
            <DickinsonCountyScraper />
          )}
          {selectedCounty !== 'dickinson' && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">
                  Automated scraping configuration for {COUNTIES.find(c => c.value === selectedCounty)?.label} coming soon.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="export" className="space-y-4">
          <DataExportTool />
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          <ResearchResults 
            searchResults={{
              records: [],
              isSearching: false,
              progress: 0,
              ownerFinanceCount: 0,
              addressesExtracted: 0
            }}
            onRefresh={handleRecordSaved}
            key={refreshTrigger}
            defaultCountyFilter={selectedCounty}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
